// @ts-nocheck
/**
 *
 * AudioFileUploader
 *
 */
import * as React from 'react';
import { useState } from 'react';
import styled from 'styled-components/macro';
import {FileUploader} from "react-drag-drop-files";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faCloudArrowUp, faHandLove} from "@fortawesome/pro-solid-svg-icons";
import pluralize from 'pluralize';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useOutletContext } from 'react-router-dom';
import { useClerk } from '@clerk/clerk-react';
import { useOpenInExternalBrowserModalSlice } from 'app/components/OpenInExternalBrowserModal/slice';
import InAppSpy from 'inapp-spy';
import { useDispatch } from 'react-redux';
import Collapsible from 'react-collapsible';
import { faChevronDown, faChevronUp } from '@fortawesome/pro-solid-svg-icons';
import IconButton from '@mui/material/IconButton';

interface Props {
  handleChange: (files: FileList) => void,
  fileWithPreviews: Array<FileWithPreview>,
  renderAudioFilePreview: (audio, index) => (undefined | JSX.Element),
  getSignedUrlEndpoint: string | undefined,
  isTraining: boolean,
  isUploading: boolean,
  small: boolean,
  hideAddFiles: boolean,
  maxFiles: number | undefined,
}

const fileTypes = ['WAV', 'm4a', 'mp3'];

export type FileWithPreview = {
  key: number;
  preview: string;
  data: File;
  signedUrl: string | null;
  uploadProgress: number | null;
  filename: string | null;
  storageBucket: string | null;
  storageFileName: string | null;
  duration?: number;
  minimalVersion?: boolean;
};

export default function AudioFileUploader(props: Props) {
  const [userProfile, isLoaded, isSignedIn] = useOutletContext();
  const clerk = useClerk();
  const dispatch = useDispatch();
  const { isInApp } = InAppSpy();
  const theme = useTheme();
  const { actions: openInExternalBrowserActions } = useOpenInExternalBrowserModalSlice();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm')) || props.small;
  const [isDragging, setIsDragging] = useState(false);
  const minimalVersion = props.minimalVersion ?? false;

  let uploadStatusText = "ready";
  if (props.isUploading) uploadStatusText = "uploading"

  const handleDropZoneClick = (e) => {
    e.stopPropagation();
    e.preventDefault();

    if (!isSignedIn) {
      // Check if in another app webview (google sign in will be blocked)
      // and prompt to open in external browser
      if (isInApp) {
        // show modal to open in browser
        dispatch(openInExternalBrowserActions.open())
      } else {
        clerk.openSignIn({ 
          mode: 'modal', 
          afterSignInUrl: window.location.href, 
          afterSignUpUrl: window.location.href 
      });
      }
    }
    console.log('drop zone clicked');
  };

  const handleDraggingStateChange = (dragging) => {
    console.log('dragging: ', dragging);
    setIsDragging(dragging);
  };

  const handleFileChange = (files) => {
    console.log('files: ', files);
    // Logged out users can't upload files
    if (!isSignedIn) {
      // Check if in another app webview (google sign in will be blocked)
      // and prompt to open in external browser
      if (isInApp) {
        // show modal to open in browser
        dispatch(openInExternalBrowserActions.open())
      } else {
        clerk.openSignIn({ 
          mode: 'modal', 
          afterSignInUrl: window.location.href, 
          afterSignUpUrl: window.location.href
        });
      }
      return;
    }
    props.handleChange(files);
  };

  const audioFileListComponent = (
    <div className="audio-file-list">
      <div className="audio-file-list-content">
        {props.fileWithPreviews.map(props.renderAudioFilePreview)}
      </div>
    </div>
  )

  return <div style={{ position: 'relative' }}>
    {/* This div is used to capture the click event and prevent it from being handled by the FileUploader */}
    {!isSignedIn && !isDragging && <div style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', zIndex: 1000, cursor: 'pointer', pointerEvents: '' }} onClick={handleDropZoneClick}>&nbsp;</div>}
    <FileUploader
        onDraggingStateChange={handleDraggingStateChange}
        handleChange={handleFileChange}
        name="file"
        types={fileTypes}
        classes="drop_area"
        multiple
        dropMessageStyle={{
          width: '100%',
        }}
    >
      {props.fileWithPreviews.length === 0 && (
        <>
          {
            isMobile ? (
            <UploadButtonMobile>
              <div className="icon">
                <FontAwesomeIcon icon={faCloudArrowUp} size={"2x"}/>
              </div>
              <div className="content">
                <p>
                  Upload
                </p>
              </div>
            </UploadButtonMobile>
          ) : minimalVersion ? (
            <MinimalStylesButton>
              <FontAwesomeIcon icon={faCloudArrowUp} style={{color: '#FF74E9'}} size='lg'/>
              <span>Upload</span>
            </MinimalStylesButton>
          ) : (
          <div className={"inference-file-input drop-zone"}>
            <div className="icon">
              <FontAwesomeIcon icon={faCloudArrowUp} size={isMobile ?  "1x" : "3x"}/>
            </div>
            <div className="content">
              <p style={{ fontSize: isMobile ? "14px" : "16px"}}>
                {" "}
                {isMobile ? "" : "Drag and drop or "}<a className="color-pink">
                Browse Files{" "}
              </a>{" "}
              </p>
              <p className="caption" style={{ fontSize: isMobile ? "14px" : "16px"}}>.WAV (44.1kHz 16-bit) Recommended </p>
              <p className="caption" style={{ fontSize: isMobile ? "12px" : "14px"}}>Also Accepts .MP3 or .M4A </p>
            </div>
          </div>
        )}
        </>
      )}
      {props.fileWithPreviews.length > 0 && !props.hideAddFiles && (props.maxFiles !== 1) && (
          <div className="selected-files-drop-zone">
            <div className="selected-files-action">Add files</div>
          </div>
      )}
    </FileUploader>
    {props.fileWithPreviews.length > 0 && (
        <>
          {/* If multiple files allowed, show in collapsible */}
          {props.maxFiles !== 1 && (
            <Collapsible
              trigger={
                <div style={{display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%", fontSize: "16px", paddingRight: "10px"}}>
                  <p style={{textAlign: "left", marginTop: "0px", marginBottom: "0px", cursor: "pointer", fontSize: "inherit" }}>
                    {props.fileWithPreviews.length} audio {pluralize("file", props.fileWithPreviews.length)}{" "}
                    {props.isTraining ? `${uploadStatusText} for training` : `${uploadStatusText} to convert`}
                  </p>
                  <IconButton size="small">
                    <FontAwesomeIcon icon={faChevronDown} />
                  </IconButton>
                </div>
              }
              triggerWhenOpen={
                <div style={{display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%", fontSize: "16px", paddingRight: "10px"}}>
                  <p style={{textAlign: "left", marginTop: "0px", marginBottom: "0px", cursor: "pointer", fontSize: "inherit"}}>
                    {props.fileWithPreviews.length} audio {pluralize("file", props.fileWithPreviews.length)}{" "}
                    {props.isTraining ? `${uploadStatusText} for training` : `${uploadStatusText} to convert`}
                  </p>
                  <IconButton size="small">
                    <FontAwesomeIcon icon={faChevronUp} />
                  </IconButton>
                </div>
              }
              open={true}
            >
              {audioFileListComponent}
            </Collapsible>
          )}
          {/* If single file, just show audio file list */}
          {props.maxFiles === 1 && (
            audioFileListComponent
          )}
        </>
    )}
  </div>;
}


const UploadButtonMobile = styled.div`
  display: flex;
  width: 100%;
  flex-direction: column;
  height: 150px;
  padding: 25px 23px;
  justify-content: center;
  align-items: center;
  align-content: center;
  gap: 10px;
  flex-wrap: wrap;
  border-radius: 20px;
  border: 2px solid var(--elevation-outlined, rgba(255, 116, 233, 0.74));
  background: linear-gradient(180deg, #07000B 0%, #180728 70.5%);

  margin-right: 60px;

  p {
    color: var(--text-primary, #FFF);
    font-family: Jura;
    font-size: 16px;
    font-style: normal;
    font-weight: 700;
    line-height: 24px; /* 150% */
    letter-spacing: 0.4px;
    text-transform: uppercase;
    margin: 0;
  }

  .icon {
    color: var(--primary-main, #FF74E9);
  }
`;

const MinimalStylesButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  background: none;
  border: none;
  color: #f5e1ff;
  font-size: 12px;
  font-family: 'Jura', sans-serif;
  font-weight: 500;
  text-transform: uppercase;
  border: 1px solid #645270;
  border-radius: 20px;
  padding: 24px 16px;
  cursor: pointer;
  width: 100%;

  &:hover {
    background: rgba(255, 255, 255, 0.04);
  }
`;